import axios from "@/utils/axios";
import type { RequestFormResponse } from "@/types/common/form";
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryClient,
} from "@tanstack/react-query";

interface GetFormParams {
  id: string;
  relationships: string[];
  formalityId: string;
}

const DEFAULT_FORM_RELATIONSHIPS = [
  "authApplicantFormAssignments",
  "authApplicantFormAssignments.executionNode.flowExecution.flow.process",
  "formAssignments",
];

export const useGetFormById = (params: GetFormParams, enabled = true) => {
  const { id, formalityId: formality } = params;

  const relationshipsNotEmpty = params.relationships?.length > 0;

  const relationships = relationshipsNotEmpty
    ? [params.relationships.join("."), ...DEFAULT_FORM_RELATIONSHIPS]
    : [];

  const queryKey = {
    id,
    formality,
    relationships,
  };

  const queryParams = {
    filter: { formality },
    ...(relationshipsNotEmpty && { relationships: relationships.join(",") }),
  };

  return useQuery<RequestFormResponse>({
    queryKey: ["get-form-by-id", queryKey],
    queryFn: async (): Promise<RequestFormResponse> => {
      const { data } = await axios.get(`/internal/forms/${id}`, {
        params: queryParams,
      });

      return {
        data: data.data,
      };
    },
    enabled: enabled && !!id,
  });
};

interface SaveFormParams {
  assignmentId: string;
  fields: Record<string, string>;
  is_final_submission: boolean;
  step_id?: string;
  save_as_draft?: boolean;
}

export const invalidateInternalFormQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ["tramite-forms"] });
  queryClient.invalidateQueries({ queryKey: ["form-detail"] });
  queryClient.removeQueries({ queryKey: ["get-form-by-id"] });
};

export const useSaveDraftForm = () => {
  return useMutation({
    mutationFn: async (data: {
      assignmentId: string;
      fields: Record<string, unknown>;
    }) => {
      const { assignmentId, fields } = data;
      const response = await axios.post(
        `/admin/v1/internal/forms/assignments/${assignmentId}/submit`,
        { fields, is_final_submission: false, save_as_draft: true },
      );
      return response.data;
    },
    gcTime: 0,
  });
};

export const useSaveForm = (options?: { skipInvalidation?: boolean }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["save-form"],
    mutationFn: async (data: Partial<SaveFormParams>) => {
      const { assignmentId, fields, is_final_submission } = data;

      if (!assignmentId) {
        throw new Error("assignmentId es requerido");
      }

      const response = await axios.post(
        `/admin/v1/internal/forms/assignments/${assignmentId}/submit`,
        { fields, is_final_submission },
      );

      return response.data;
    },
    onSuccess: (variables) => {
      if (!options?.skipInvalidation) {
        invalidateInternalFormQueries(queryClient);
      }

      console.log("Formulario guardado para el ID:", variables.assignmentId);
    },
  });
};

// ---------------- Documentos ----------------

type UploadFieldParams = {
  assignmentId: string;
  fieldId: string;
  files: File[] | File;
  formUploadKey?: string;
  filename?: string;
  onProgress?: (pct: number) => void;
};

type UploadFieldResponse = { data: unknown };

export const useUploadFormField = (options?: {
  skipInvalidation?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useMutation<UploadFieldResponse, unknown, UploadFieldParams>({
    mutationKey: ["upload-form-field"],
    mutationFn: async ({
      assignmentId,
      fieldId,
      files,
      formUploadKey = "file[]",
      filename,
      onProgress,
    }): Promise<UploadFieldResponse> => {
      if (!assignmentId || !fieldId) {
        throw new Error("assignmentId y fieldId son requeridos");
      }

      const form = new FormData();

      const appendOne = (f: File) => {
        form.append(formUploadKey, f, f.name || filename || "upload.bin");
      };

      if (Array.isArray(files)) {
        files.forEach(appendOne);
      } else {
        appendOne(files);
      }

      const { data } = await axios.post(
        `/admin/v1/internal/forms/assignments/${assignmentId}/fields/${fieldId}/upload`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (onProgress && e.total) {
              const pct = Math.round((e.loaded * 100) / e.total);
              onProgress(pct);
            }
          },
        },
      );

      return { data };
    },
    onSuccess: () => {
      if (!options?.skipInvalidation) {
        invalidateInternalFormQueries(queryClient);
      }
    },
  });
};

// Asignar formulario a usuario
export const useAssignMe = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { form_id: string }) => {
      const { data } = await axios.post(
        `/admin/v1/internal/forms/assign/me`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tramite-forms"] });
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
      queryClient.invalidateQueries({ queryKey: ["get-form-by-id"] });
    },
  });
};

// obtener form assignments
export const useGetFormAssignments = () => {
  return useQuery({
    queryKey: ["get-form-assignments"],
    queryFn: async () => {
      const { data } = await axios.get("/admin/v1/internal/forms/assignments");
      return data;
    },
  });
};

// obtener assignment por id
export const useGetAssignmentById = (assignmentId: string) => {
  return useQuery({
    queryKey: ["get-assignment-by-id", assignmentId],
    queryFn: async () => {
      const { data } = await axios.get(
        `/admin/v1/internal/forms/assignments/${assignmentId}`,
      );
      return data;
    },
    enabled: !!assignmentId,
  });
};

// Iniciar formulario

export const useStartForm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignmentId: string) => {
      const { data } = await axios.post(
        `/admin/v1/internal/forms/assignments/${assignmentId}/start`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tramite-forms"] });
      queryClient.invalidateQueries({ queryKey: ["form-detail"] });
      queryClient.invalidateQueries({ queryKey: ["get-form-by-id"] });
    },
  });
};
